'use client';

import { Brain, Search, MessageSquare, RefreshCw } from 'lucide-react';
import { EFeedback, StreamStatusEvent, AgentActivity, Citation, TokenUsage, IDataPoolEntry, ICitation } from '@/types';
import { useState, useEffect, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import MarkdownViewer from '@/_components/MarkdownViewer/MarkdownViewer';
import AgentActivityLog from '@/_components/AgentActivityLog/AgentActivityLog';
import ProvenanceTab from '@/_components/ProvenanceTab/ProvenanceTab';
import TokenUsageDisplay from '@/_components/TokenUsageDisplay/TokenUsageDisplay';
import FeedbackButtons from '@/_components/FeedbackButtons/FeedbackButtons';
import { FollowUpSuggestions } from '@/_components/FollowUpSuggestions/FollowUpSuggestions';
import { useMessageDetailsQuery, useMessagesCache } from '@/_state/queries';
import { currentConversationIdAtom } from '@/_state/atoms';

interface AssistantMessageWithTabsProps {
	message: string;
	isStreaming?: boolean;
	currentStatus?: StreamStatusEvent | null;
	/** Model thinking/reasoning text streamed in real-time */
	thinkingText?: string;
	activities: AgentActivity[];
	citations: Citation[];
	dataPoolEntries?: IDataPoolEntry[];
	tokenUsage?: TokenUsage | null;
	/** Message ID for feedback tracking */
	messageId?: string;
	/** Current feedback state */
	feedback?: EFeedback | null;
	/** Read-only mode - hides feedback buttons (for public viewers) */
	readOnly?: boolean;
	/** Whether details (activities, provenance, citations) need to be lazy-loaded */
	needsLazyLoad?: boolean;
	/** Callback to regenerate this AI response */
	onRegenerate?: (messageId: string) => void;
	/** Follow-up suggestion questions */
	suggestions?: string[];
	/** Callback when a suggestion chip is clicked */
	onSuggestionClick?: (suggestion: string) => void;
}

type TabType = 'thinking' | 'provenance' | 'response';

// Mobile-first tab classes with proper touch targets (min 44px height)
const TAB_BASE_CLASS =
	'flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 min-h-[44px]';
const SHIMMER_BAR_CLASS =
	'bg-secondary/60 before:via-accent/30 relative overflow-hidden rounded before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:to-transparent';
const TAB_ACTIVE_CLASS = 'border-b-2 border-blue-500 bg-secondary text-foreground';
const TAB_INACTIVE_CLASS = 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground';

export default function AssistantMessageWithTabs({
	message,
	isStreaming,
	currentStatus,
	thinkingText,
	activities,
	citations,
	dataPoolEntries,
	tokenUsage,
	messageId,
	feedback,
	readOnly = false,
	needsLazyLoad = false,
	onRegenerate,
	suggestions,
	onSuggestionClick
}: AssistantMessageWithTabsProps) {
	const [activeTab, setActiveTab] = useState<TabType>('thinking');
	const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
	const [highlightedPath, setHighlightedPath] = useState<string | null>(null);
	// Citation scroll request with timestamp to force re-scroll on same citation
	const [scrollRequest, setScrollRequest] = useState<{ index: number; timestamp: number } | null>(null);
	// Track if we've triggered lazy load
	const [hasTriggeredLazyLoad, setHasTriggeredLazyLoad] = useState(false);

	const conversationId = useAtomValue(currentConversationIdAtom);
	const { updateMessage } = useMessagesCache();

	// Lazy load message details when needed
	const {
		data: lazyLoadedDetails,
		isFetching: isLoadingDetails,
		refetch: loadDetails
	} = useMessageDetailsQuery(messageId, { enabled: false });

	// Use lazy-loaded data if available, otherwise use props
	const effectiveActivities = lazyLoadedDetails?.agentActivities ?? activities;
	const effectiveDataPoolEntries = lazyLoadedDetails?.dataPoolEntries ?? dataPoolEntries ?? [];
	// Convert dbCitations (ICitation[]) to display Citations when lazy-loaded
	const effectiveCitations: Citation[] = lazyLoadedDetails?.dbCitations
		? lazyLoadedDetails.dbCitations.map((c: ICitation) => ({ index: c.citationIndex, jsonPath: c.jsonPath }))
		: citations;
	const effectiveTokenUsage = lazyLoadedDetails?.tokenUsage ?? tokenUsage;

	// Auto-switch to Response tab when content is substantial

	useEffect(() => {
		if (!hasAutoSwitched && message && message.length > 50) {
			setActiveTab('response');
			setHasAutoSwitched(true);
		}
	}, [message, hasAutoSwitched]);

	// Reset auto-switch flag when a new message starts streaming

	useEffect(() => {
		if (isStreaming && !message) {
			setHasAutoSwitched(false);
			setActiveTab('thinking');
		}
	}, [isStreaming, message]);

	// Auto-load details for historical messages (ensures citations are available for Response tab)
	useEffect(() => {
		if (needsLazyLoad && !isStreaming && !hasTriggeredLazyLoad && !lazyLoadedDetails) {
			loadDetails();
			setHasTriggeredLazyLoad(true);
		}
	}, [needsLazyLoad, isStreaming, hasTriggeredLazyLoad, lazyLoadedDetails, loadDetails]);

	// Sync lazy-loaded details back to the main messages cache so parent components
	// (e.g. Conversation.tsx refined query annotation) can access agentActivities reactively.
	// Skip during streaming to avoid overwriting with stale data while new activities arrive.
	useEffect(() => {
		if (lazyLoadedDetails && conversationId && messageId && !isStreaming) {
			updateMessage(conversationId, messageId, {
				agentActivities: lazyLoadedDetails.agentActivities,
				tokenUsage: lazyLoadedDetails.tokenUsage ?? null
			});
		}
	}, [lazyLoadedDetails, conversationId, messageId, updateMessage, isStreaming]);

	// Handle citation click - switch to provenance tab and scroll to the citation
	const handleCitationClick = useCallback(
		(citationIndex: number) => {
			// Trigger lazy load if details not loaded (needed for dataPoolEntries in Provenance tab)
			if (messageId && messageId !== 'streaming' && !lazyLoadedDetails && !isLoadingDetails && !hasTriggeredLazyLoad) {
				loadDetails();
				setHasTriggeredLazyLoad(true);
			}

			const citation = effectiveCitations.find((c) => c.index === citationIndex);
			if (!citation) return;

			setActiveTab('provenance');
			setHighlightedPath(citation.jsonPath);
			// Use timestamp to force scroll even when clicking same citation
			setScrollRequest({ index: citationIndex, timestamp: Date.now() });

			// Clear highlight after 5 seconds
			setTimeout(() => {
				setHighlightedPath(null);
				setScrollRequest(null);
			}, 5000);
		},
		[effectiveCitations, messageId, lazyLoadedDetails, isLoadingDetails, hasTriggeredLazyLoad, loadDetails]
	);

	// Handle citation tooltip hover - trigger lazy load for provenance data
	const handleRequestDetails = useCallback(() => {
		if (messageId && messageId !== 'streaming' && !lazyLoadedDetails && !isLoadingDetails && !hasTriggeredLazyLoad) {
			loadDetails();
			setHasTriggeredLazyLoad(true);
		}
	}, [messageId, lazyLoadedDetails, isLoadingDetails, hasTriggeredLazyLoad, loadDetails]);

	// Handle tab click with lazy loading
	const handleTabClick = useCallback(
		(tab: TabType) => {
			setActiveTab(tab);
			// Trigger lazy load when switching to provenance/thinking if details not yet loaded
			// This covers both lightweight server-loaded messages AND just-streamed messages
			// (which have agentActivities but no dataPoolEntries/dbCitations)
			if (
				(tab === 'provenance' || tab === 'thinking') &&
				messageId &&
				messageId !== 'streaming' &&
				!lazyLoadedDetails &&
				!isLoadingDetails &&
				!hasTriggeredLazyLoad
			) {
				loadDetails();
				setHasTriggeredLazyLoad(true);
			}
		},
		[messageId, lazyLoadedDetails, isLoadingDetails, hasTriggeredLazyLoad, loadDetails]
	);

	const getTabClass = (tab: TabType) =>
		`${TAB_BASE_CLASS} ${activeTab === tab ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS}`;

	const hasThinkingContent = currentStatus || effectiveActivities.length > 0 || !!thinkingText;

	// Lazy loading skeleton JSX - matches the streaming skeleton
	const lazyLoadingSkeleton = (
		<div className='space-y-3 py-4'>
			<div className='flex items-center gap-2'>
				<div className={`h-4 w-4 rounded-full ${SHIMMER_BAR_CLASS}`} />
				<div className={`h-4 w-32 ${SHIMMER_BAR_CLASS}`} />
			</div>
			{[1, 2, 3].map((i) => (
				<div
					key={i}
					className='flex items-start gap-3'
				>
					<div className={`h-5 w-5 flex-shrink-0 ${SHIMMER_BAR_CLASS}`} />
					<div className='flex-1 space-y-2'>
						<div className={`h-4 w-3/4 ${SHIMMER_BAR_CLASS}`} />
						<div className={`h-3 w-1/2 ${SHIMMER_BAR_CLASS}`} />
					</div>
				</div>
			))}
		</div>
	);

	return (
		<div className='bg-card mr-auto w-full max-w-full rounded-lg border shadow-sm'>
			{/* Tab Headers - scrollable on mobile */}
			<div className='scrollbar-none border-border flex overflow-x-auto border-b'>
				<button
					type='button'
					onClick={() => handleTabClick('thinking')}
					className={getTabClass('thinking')}
				>
					<Brain
						className='h-4 w-4 flex-shrink-0'
						strokeWidth={1.5}
					/>
					<span className='sm:inline'>Thinking</span>
					{isStreaming && hasThinkingContent && (
						<span className='ml-1 inline-flex h-2 w-2 animate-pulse rounded-full bg-blue-500' />
					)}
				</button>
				<button
					type='button'
					onClick={() => handleTabClick('provenance')}
					className={getTabClass('provenance')}
				>
					<Search
						className='h-4 w-4 flex-shrink-0'
						strokeWidth={1.5}
					/>
					<span className='sm:hidden'>Data</span>
					<span className='hidden sm:inline'>Provenance</span>
					{effectiveCitations.length > 0 && (
						<span className='bg-secondary text-foreground ml-1 rounded-full px-1.5 text-xs font-medium'>
							{effectiveCitations.length}
						</span>
					)}
				</button>
				<button
					type='button'
					onClick={() => handleTabClick('response')}
					className={getTabClass('response')}
				>
					<MessageSquare
						className='h-4 w-4 flex-shrink-0'
						strokeWidth={1.5}
					/>
					<span className='sm:inline'>Response</span>
					{isStreaming && message && <span className='ml-1 inline-flex h-2 w-2 rounded-full bg-green-500' />}
				</button>
			</div>

			{/* Tab Content */}
			<div className='p-2 sm:p-4'>
				{activeTab === 'thinking' && (
					<div className='animate-[fadeSlideIn_0.15s_ease-out] space-y-4'>
						{isLoadingDetails ? (
							lazyLoadingSkeleton
						) : effectiveActivities.length > 0 || thinkingText ? (
							<AgentActivityLog
								activities={effectiveActivities}
								thinkingText={thinkingText}
								isStreaming={isStreaming}
							/>
						) : isStreaming ? (
							lazyLoadingSkeleton
						) : (
							<div className='text-muted-foreground text-sm'>
								<p>No agent activity yet.</p>
							</div>
						)}
						{/* Token usage footer */}
						{effectiveTokenUsage && (
							<div className='border-border mt-4 border-t pt-3'>
								<TokenUsageDisplay tokenUsage={effectiveTokenUsage} />
							</div>
						)}
					</div>
				)}

				{activeTab === 'provenance' &&
					(isLoadingDetails ? (
						lazyLoadingSkeleton
					) : (
						<div className='animate-[fadeSlideIn_0.15s_ease-out]'>
							<ProvenanceTab
								dataPoolEntries={effectiveDataPoolEntries}
								citations={effectiveCitations}
								highlightedPath={highlightedPath}
								scrollRequest={scrollRequest}
								onCitationClick={(path) => setHighlightedPath(path)}
							/>
						</div>
					))}

				{activeTab === 'response' && (
					<div className='text-foreground animate-[fadeSlideIn_0.15s_ease-out]'>
						{!message && isStreaming ? (
							<div className='space-y-3'>
								<div className='text-muted-foreground flex items-center gap-2'>
									<span className='border-muted-foreground inline-block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent' />
									<span className='animate-pulse'>Generating Response...</span>
								</div>
								<div className='space-y-2 pt-1'>
									<div className={`h-3.5 w-full ${SHIMMER_BAR_CLASS}`} />
									<div className={`h-3.5 w-4/5 ${SHIMMER_BAR_CLASS}`} />
									<div className={`h-3.5 w-3/5 ${SHIMMER_BAR_CLASS}`} />
								</div>
							</div>
						) : message ? (
							<div>
								<MarkdownViewer
									content={message}
									citations={effectiveCitations}
									dataPoolEntries={effectiveDataPoolEntries}
									onCitationClick={handleCitationClick}
									onRequestDetails={handleRequestDetails}
								/>
								{isStreaming && <span className='bg-foreground ml-1 inline-block h-4 w-2' />}
								{/* Follow-up suggestions - shown after streaming completes */}
								{!isStreaming && suggestions && suggestions.length > 0 && onSuggestionClick && (
									<FollowUpSuggestions
										suggestions={suggestions}
										onSuggestionClick={onSuggestionClick}
									/>
								)}
								{/* Feedback and regenerate - shown when not streaming */}
								{!isStreaming && messageId && messageId !== 'streaming' && !readOnly && (
									<div className='border-border mt-4 flex items-center justify-between border-t pt-3'>
										<span className='text-muted-foreground text-xs'>Was this helpful?</span>
										<div className='flex items-center gap-2'>
											{onRegenerate && (
												<button
													type='button'
													onClick={() => onRegenerate(messageId)}
													className='text-muted-foreground hover:bg-accent hover:text-foreground flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors'
													aria-label='Regenerate response'
												>
													<RefreshCw className='h-3.5 w-3.5' />
													Regenerate
												</button>
											)}
											<FeedbackButtons
												messageId={messageId}
												currentFeedback={feedback}
											/>
										</div>
									</div>
								)}
							</div>
						) : (
							<div className='text-muted-foreground'>
								<p>Waiting for response...</p>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
